import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./auth/auth-provider";
import { TenetLogoLoader } from "@tenet/web-ui";

const Layout = lazy(() =>
  import("./components/layout").then((module) => ({ default: module.Layout })),
);
const Login = lazy(() =>
  import("./pages/login").then((module) => ({ default: module.Login })),
);
const Signup = lazy(() =>
  import("./pages/signup").then((module) => ({ default: module.Signup })),
);
const Callback = lazy(() =>
  import("./pages/callback").then((module) => ({ default: module.Callback })),
);
const Dashboard = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.Dashboard })),
);
const Organisation = lazy(() =>
  import("./pages/workspace/organisation/overview").then((module) => ({
    default: module.Organisation,
  })),
);
const Rules = lazy(() =>
  import("./pages/workspace/organisation/rules").then((module) => ({ default: module.Rules })),
);
const Projects = lazy(() =>
  import("./pages/workspace/organisation/projects").then((module) => ({ default: module.Projects })),
);
const ProjectDetail = lazy(() =>
  import("./pages/workspace/project/overview").then((module) => ({
    default: module.ProjectDetail,
  })),
);
const QualityAttributes = lazy(() =>
  import("./pages/workspace/project/quality-attributes").then((module) => ({
    default: module.QualityAttributes,
  })),
);
const Skills = lazy(() =>
  import("./pages/workspace/project/skills").then((module) => ({ default: module.Skills })),
);
const Specs = lazy(() =>
  import("./pages/workspace/project/specs").then((module) => ({ default: module.Specs })),
);
const ProjectRules = lazy(() =>
  import("./pages/workspace/project/rules").then((module) => ({
    default: module.ProjectRules,
  })),
);
const ProjectActors = lazy(() =>
  import("./pages/workspace/project/actors").then((module) => ({
    default: module.ProjectActors,
  })),
);
const ProjectExternalSystems = lazy(() =>
  import("./pages/workspace/project/external-systems").then((module) => ({
    default: module.ProjectExternalSystems,
  })),
);
const ProjectStores = lazy(() =>
  import("./pages/workspace/project/stores").then((module) => ({
    default: module.ProjectStores,
  })),
);
const ProjectContainers = lazy(() =>
  import("./pages/workspace/project/containers").then((module) => ({
    default: module.ProjectContainers,
  })),
);
const ProjectComponents = lazy(() =>
  import("./pages/workspace/project/components").then((module) => ({
    default: module.ProjectComponents,
  })),
);
const AcceptInvite = lazy(() =>
  import("./pages/accept-invite").then((module) => ({
    default: module.AcceptInvite,
  })),
);
const OAuthAuthorize = lazy(() =>
  import("./pages/oauth-authorize").then((module) => ({
    default: module.OAuthAuthorize,
  })),
);

// Loading spinner component
const LoadingSpinner = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <TenetLogoLoader size="lg" />
    </div>
  );
};

const withSuspense = (children: ReactNode) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper (redirects to dashboard if already authenticated)
// Note: We intentionally don't show LoadingSpinner here during auth operations
// because it would unmount the children (e.g., Signup) and reset their local state.
// The child components handle their own loading states via the isLoading from useAuth.
const PublicRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  // Get return URL from query params
  const returnTo = searchParams.get("returnTo");

  // Only check authentication after initial auth check is complete
  // This prevents redirecting before we know the auth state
  if (!isLoading && isAuthenticated) {
    // If there's a return URL, go there instead of dashboard
    const redirectTo = returnTo || "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// Semi-public route for invite acceptance
// Shows the page regardless of auth state (user might need to login/signup)
const InviteRoute = ({ children }: { children: ReactNode }) => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

export const App = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={<PublicRoute>{withSuspense(<Login />)}</PublicRoute>}
      />
      <Route
        path="/signup"
        element={<PublicRoute>{withSuspense(<Signup />)}</PublicRoute>}
      />
      <Route path="/callback" element={withSuspense(<Callback />)} />
      <Route
        path="/oauth/authorize"
        element={withSuspense(<OAuthAuthorize />)}
      />

      {/* Semi-public invite acceptance route */}
      <Route
        path="/invite/:token"
        element={<InviteRoute>{withSuspense(<AcceptInvite />)}</InviteRoute>}
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={<ProtectedRoute>{withSuspense(<Layout />)}</ProtectedRoute>}
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={withSuspense(<Dashboard />)} />
        <Route path="organisation" element={withSuspense(<Organisation />)} />
        <Route path="rules" element={withSuspense(<Rules />)} />
        <Route path="rules/new" element={withSuspense(<Rules />)} />
        <Route path="rules/:ruleId" element={withSuspense(<Rules />)} />
        <Route path="rules/:ruleId/edit" element={withSuspense(<Rules />)} />
        <Route path="projects" element={withSuspense(<Projects />)} />
        <Route path="projects/new" element={withSuspense(<Projects />)} />
        <Route
          path="projects/:projectId/edit"
          element={withSuspense(<Projects />)}
        />
        <Route
          path="projects/:projectId"
          element={withSuspense(<ProjectDetail />)}
        />
        <Route
          path="projects/:projectId/quality-attributes"
          element={withSuspense(<QualityAttributes />)}
        />
        <Route
          path="projects/:projectId/quality-attributes/:qualityAttributeId"
          element={withSuspense(<QualityAttributes />)}
        />
        <Route
          path="projects/:projectId/skills"
          element={withSuspense(<Skills />)}
        />
        <Route
          path="projects/:projectId/skills/:skillId"
          element={withSuspense(<Skills />)}
        />
        <Route
          path="projects/:projectId/specs"
          element={withSuspense(<Specs />)}
        />
        <Route
          path="projects/:projectId/specs/new"
          element={withSuspense(<Specs />)}
        />
        <Route
          path="projects/:projectId/specs/:specId"
          element={withSuspense(<Specs />)}
        />
        <Route
          path="projects/:projectId/rules"
          element={withSuspense(<ProjectRules />)}
        />
        <Route
          path="projects/:projectId/rules/:projectRuleId"
          element={withSuspense(<ProjectRules />)}
        />
        <Route
          path="projects/:projectId/actors"
          element={withSuspense(<ProjectActors />)}
        />
        <Route
          path="projects/:projectId/actors/:actorId"
          element={withSuspense(<ProjectActors />)}
        />
        <Route
          path="projects/:projectId/external-systems"
          element={withSuspense(<ProjectExternalSystems />)}
        />
        <Route
          path="projects/:projectId/external-systems/:externalSystemId"
          element={withSuspense(<ProjectExternalSystems />)}
        />
        <Route
          path="projects/:projectId/stores"
          element={withSuspense(<ProjectStores />)}
        />
        <Route
          path="projects/:projectId/stores/:storeId"
          element={withSuspense(<ProjectStores />)}
        />
        <Route
          path="projects/:projectId/containers"
          element={withSuspense(<ProjectContainers />)}
        />
        <Route
          path="projects/:projectId/containers/:containerId"
          element={withSuspense(<ProjectContainers />)}
        />
        <Route
          path="projects/:projectId/components"
          element={withSuspense(<ProjectComponents />)}
        />
        <Route
          path="projects/:projectId/components/:componentId"
          element={withSuspense(<ProjectComponents />)}
        />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};
